/* eslint-disable react/prop-types */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Tag,
  Plus,
  Loader2,
  Trash2,
  Pencil,
  Search,
  Eye,
  EyeOff,
  Copy,
  AlertTriangle,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { fetchVendorCoupons, createCoupon, updateCoupon, deleteCoupon } from './api/CouponApi';
import { inventoryApi } from './api/InventoryApi';
import axiosInstance from '@/utils/axiosInstance';
import { getAuthHeaders } from '@/utils/api';
import { API_URL, USER_ROLES } from '@/constant/constant';
import { useUser } from '@/contexts/UserContext';
import BottomNav from '../../components/layouts/Footer/BottomNav';

const COUPON_TYPES = [
  { value: 'FIXED', label: 'Fixed Amount (₹)' },
  { value: 'PERCENTAGE', label: 'Percentage (%)' },
];

const DAYS_OF_WEEK = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
];

const defaultForm = {
  id: null,
  code: '',
  name: '',
  description: '',
  couponType: 'FIXED',
  value: '',
  minOrderAmount: '',
  minParticipants: '',
  oneTimePerUser: false,
  validFrom: '',
  validUntil: '',
  applicableDays: [],
  dailyStartTime: '',
  dailyEndTime: '',
  totalUsageLimit: '',
  perUserLimit: '',
  perUserPerDay: false,
  maxDiscountAmount: '',
  productIds: [],
  visibleToAll: true,
};

const toInputDateTime = (value) => (value ? String(value).slice(0, 16) : '');
const toInputTime = (value) => (value ? String(value).slice(0, 5) : '');

// Collects the leaf category nodes of a vendor category tree — the products
// endpoint only returns products for leaf categories.
const collectLeafNodes = (node) => {
  if (!node) return [];
  const subs = node.subCategories || [];
  return subs.length === 0
    ? [{ id: node.id, name: node.name }].filter((leaf) => leaf.id)
    : subs.flatMap(collectLeafNodes);
};

const buildPayload = (coupon) => ({
  code: (coupon.code || '').trim().toUpperCase(),
  name: (coupon.name || '').trim(),
  description: (coupon.description || '').trim(),
  couponType: coupon.couponType,
  value: Number(coupon.value),
  minOrderAmount: Number(coupon.minOrderAmount || 0),
  minParticipants: Number(coupon.minParticipants || 0),
  oneTimePerUser: !!coupon.oneTimePerUser,
  validFrom: coupon.validFrom || null,
  validUntil: coupon.validUntil || null,
  applicableDays: coupon.applicableDays || [],
  dailyStartTime: coupon.dailyStartTime || null,
  dailyEndTime: coupon.dailyEndTime || null,
  totalUsageLimit: Number(coupon.totalUsageLimit || 0),
  perUserLimit: Number(coupon.perUserLimit || 0),
  perUserPerDay: !!coupon.perUserPerDay,
  maxDiscountAmount:
    coupon.maxDiscountAmount !== '' && coupon.maxDiscountAmount != null
      ? Number(coupon.maxDiscountAmount)
      : null,
  productIds: coupon.productIds || [],
  visibleToAll: !!coupon.visibleToAll,
});

const scheduleSummary = (coupon) => {
  const parts = [];
  if (coupon.applicableDays?.length) parts.push(coupon.applicableDays.join(', '));
  if (coupon.dailyStartTime || coupon.dailyEndTime) {
    parts.push(`${toInputTime(coupon.dailyStartTime) || '00:00'}-${toInputTime(coupon.dailyEndTime) || '23:59'}`);
  }
  if (coupon.validFrom || coupon.validUntil) {
    const from = coupon.validFrom ? new Date(coupon.validFrom).toLocaleDateString('en-IN') : 'now';
    const until = coupon.validUntil ? new Date(coupon.validUntil).toLocaleDateString('en-IN') : 'open';
    parts.push(`${from} → ${until}`);
  }
  return parts.join(' • ');
};

export default function CouponManagementPage() {
  const navigate = useNavigate();
  const { user, isInitialized } = useUser();

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [pickerCategory, setPickerCategory] = useState('');
  const vendorId = user?.id;

  // Loads all vendor products across the vendor's leaf categories so coupons
  // can be scoped to specific products.
  const loadVendorProducts = useCallback(async () => {
    if (!vendorId) return;
    setLoadingProducts(true);
    try {
      // Vendor categories come in two trees — SHOP and EVENT. A coupon can
      // scope to products under either, so fetch both.
      const [shopRes, eventRes] = await Promise.all(
        ['SHOP', 'EVENT'].map((type) =>
          axiosInstance.get(
            `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/store/categories`,
            {
              params: { vendorId, type },
              headers: { ...getAuthHeaders(), 'X-USER-ROLE': USER_ROLES.VENDOR },
            },
          ),
        ),
      );
      const categories = [...(shopRes?.data || []), ...(eventRes?.data || [])];
      const leafNodes = categories.flatMap(collectLeafNodes);
      const results = await Promise.all(
        leafNodes.map((leaf) =>
          inventoryApi
            .fetchProductsByCategory(vendorId, null, leaf.id)
            .then((result) => ({ categoryName: leaf.name, result })),
        ),
      );
      const groups = [];
      results.forEach(({ categoryName, result }) => {
        const products = (result?.data || []).filter(
          (product) =>
            product?.productId &&
            (product?.productVariants || []).some((v) => v?.variantId),
        );
        if (products.length > 0) {
          groups.push({ categoryName, products });
        }
      });
      setVendorProducts(groups);
    } catch (error) {
      console.error('Error loading vendor products:', error);
      setVendorProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [vendorId]);

  const openForm = (nextForm = defaultForm) => {
    setForm(nextForm);
    setShowForm(true);
    setPickerCategory('');
    if (vendorProducts.length === 0) {
      loadVendorProducts();
    }
  };

  const loadCoupons = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const data = await fetchVendorCoupons(vendorId);
      setCoupons(data || []);
    } catch (error) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    if (isInitialized && vendorId) {
      loadCoupons();
    }
  }, [vendorId, isInitialized, loadCoupons]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDayToggle = (day) => {
    setForm((prev) => ({
      ...prev,
      applicableDays: prev.applicableDays.includes(day)
        ? prev.applicableDays.filter((d) => d !== day)
        : [...prev.applicableDays, day],
    }));
  };

  const handleProductToggle = (productId) => {
    setForm((prev) => ({
      ...prev,
      productIds: prev.productIds.includes(productId)
        ? prev.productIds.filter((id) => id !== productId)
        : [...prev.productIds, productId],
    }));
  };

  const validate = () => {
    if (!form.code.trim()) return 'Coupon code is required';
    if (!form.name.trim()) return 'Name is required';
    if (!form.value || Number(form.value) <= 0) return 'Value must be greater than 0';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    const payload = buildPayload(form);

    try {
      if (form.id) {
        await updateCoupon(form.id, payload);
        toast.success('Coupon updated successfully');
      } else {
        await createCoupon(vendorId, payload);
        toast.success('Coupon created successfully');
      }
      setForm(defaultForm);
      setShowForm(false);
      loadCoupons();
    } catch (error) {
      toast.error(error?.response?.data || 'Failed to save coupon');
    }
  };

  const handleEdit = (coupon) => {
    openForm({
      ...defaultForm,
      ...coupon,
      value: String(coupon.value),
      minOrderAmount: String(coupon.minOrderAmount || 0),
      minParticipants: String(coupon.minParticipants || 0),
      validFrom: toInputDateTime(coupon.validFrom),
      validUntil: toInputDateTime(coupon.validUntil),
      applicableDays: coupon.applicableDays || [],
      dailyStartTime: toInputTime(coupon.dailyStartTime),
      dailyEndTime: toInputTime(coupon.dailyEndTime),
      totalUsageLimit: String(coupon.totalUsageLimit || 0),
      perUserLimit: String(coupon.perUserLimit || 0),
      perUserPerDay: !!coupon.perUserPerDay,
      maxDiscountAmount:
        coupon.maxDiscountAmount != null ? String(coupon.maxDiscountAmount) : '',
      productIds: coupon.productIds || [],
    });
  };

  const handleDelete = async (couponId) => {
    try {
      await deleteCoupon(couponId);
      toast.success('Coupon deleted successfully');
      setDeleteConfirmId(null);
      loadCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleToggleVisibility = async (coupon) => {
    try {
      const payload = buildPayload({
        ...coupon,
        visibleToAll: !coupon.visibleToAll,
      });
      await updateCoupon(coupon.id, payload);
      toast.success(
        `Coupon is now ${!coupon.visibleToAll ? 'visible to all' : 'hidden from list'}`
      );
      loadCoupons();
    } catch (error) {
      toast.error(error?.response?.data || 'Failed to update visibility');
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const selectedGroup = vendorProducts.find(
    (group) => group.categoryName === pickerCategory,
  );
  const allInGroupSelected =
    !!selectedGroup &&
    selectedGroup.products.every((p) => form.productIds.includes(p.productId));

  const handleSelectAllInGroup = () => {
    if (!selectedGroup) return;
    const groupIds = selectedGroup.products.map((p) => p.productId);
    setForm((prev) => ({
      ...prev,
      productIds: allInGroupSelected
        ? prev.productIds.filter((id) => !groupIds.includes(id))
        : [...new Set([...prev.productIds, ...groupIds])],
    }));
  };

  const filteredCoupons = coupons.filter(
    (coupon) =>
      coupon.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coupon.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg">
                  <Tag className="w-6 h-6 text-white" />
                </div>
                Coupon Management
              </h1>
              {user?.shopName && <p className="text-sm text-gray-500 mt-1">{user.shopName}</p>}
            </div>
            {vendorId && (
              <Button
                onClick={() => openForm()}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Coupon
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 pb-20 md:pb-8">
        {/* Create/Edit Form */}
        {showForm && (
          <Card className="mb-8 shadow-lg border-slate-200">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-50 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-gray-900">
                  {form.id ? 'Edit Coupon' : 'Create New Coupon'}
                </CardTitle>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Coupon Code */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Coupon Code *
                    </label>
                    <Input
                      type="text"
                      name="code"
                      value={form.code}
                      onChange={handleChange}
                      placeholder="e.g., SAVE20"
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Code will be automatically converted to uppercase
                    </p>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Coupon Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="e.g., 20% Off"
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <Input
                      type="text"
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      placeholder="Optional description for customers"
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Coupon Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Discount Type *
                    </label>
                    <select
                      name="couponType"
                      value={form.couponType}
                      onChange={handleChange}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    >
                      {COUPON_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Value */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Value ({form.couponType === 'PERCENTAGE' ? '%' : '₹'}) *
                    </label>
                    <Input
                      type="number"
                      name="value"
                      value={form.value}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Min Order Amount */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Minimum Order Amount (₹)
                    </label>
                    <Input
                      type="number"
                      name="minOrderAmount"
                      value={form.minOrderAmount}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Min Participants */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Minimum Participants
                    </label>
                    <Input
                      type="number"
                      name="minParticipants"
                      value={form.minParticipants}
                      onChange={handleChange}
                      min="0"
                      placeholder="0"
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Max Discount Cap (PERCENTAGE only) */}
                  {form.couponType === 'PERCENTAGE' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Max Discount Cap (₹)
                      </label>
                      <Input
                        type="number"
                        name="maxDiscountAmount"
                        value={form.maxDiscountAmount}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        placeholder="No cap"
                        className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  )}

                  {/* Total Usage Limit */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Total Usage Limit
                    </label>
                    <Input
                      type="number"
                      name="totalUsageLimit"
                      value={form.totalUsageLimit}
                      onChange={handleChange}
                      min="0"
                      placeholder="0 = unlimited"
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Max redemptions across all customers
                    </p>
                  </div>

                  {/* Per User Limit */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Usage Limit Per Customer
                    </label>
                    <Input
                      type="number"
                      name="perUserLimit"
                      value={form.perUserLimit}
                      onChange={handleChange}
                      min="0"
                      placeholder="0 = unlimited"
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lifetime redemptions per customer
                    </p>
                  </div>

                  {/* Valid From */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Valid From
                    </label>
                    <Input
                      type="datetime-local"
                      name="validFrom"
                      value={form.validFrom}
                      onChange={handleChange}
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Valid Until */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Valid Until
                    </label>
                    <Input
                      type="datetime-local"
                      name="validUntil"
                      value={form.validUntil}
                      onChange={handleChange}
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Daily Start Time */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Daily Start Time
                    </label>
                    <Input
                      type="time"
                      name="dailyStartTime"
                      value={form.dailyStartTime}
                      onChange={handleChange}
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Daily End Time */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Daily End Time
                    </label>
                    <Input
                      type="time"
                      name="dailyEndTime"
                      value={form.dailyEndTime}
                      onChange={handleChange}
                      className="border-slate-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* Applicable Days */}
                <div className="pt-4 border-t border-slate-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Applicable Days
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => {
                      const selected = form.applicableDays.includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => handleDayToggle(day.value)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                            selected
                              ? 'bg-orange-500 text-white border-orange-500'
                              : 'bg-white text-gray-600 border-slate-300 hover:border-orange-400'
                          }`}
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Leave all unselected for every-day validity
                  </p>
                </div>

                {/* Product Scope */}
                <div className="pt-4 border-t border-slate-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Apply To Specific Products
                    {form.productIds.length > 0 && (
                      <span className="ml-1 font-normal text-orange-600">
                        ({form.productIds.length} selected)
                      </span>
                    )}
                  </label>
                  {loadingProducts ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                      Loading products...
                    </div>
                  ) : vendorProducts.length > 0 ? (
                    <div className="space-y-3">
                      <select
                        value={pickerCategory}
                        onChange={(e) => setPickerCategory(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      >
                        <option value="">Select a category</option>
                        {vendorProducts.map((group) => (
                          <option key={group.categoryName} value={group.categoryName}>
                            {group.categoryName} ({group.products.length})
                          </option>
                        ))}
                      </select>
                      {selectedGroup && (
                        <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                          <label className="flex items-center gap-3 px-3 py-2 bg-slate-50 cursor-pointer sticky top-0">
                            <input
                              type="checkbox"
                              checked={allInGroupSelected}
                              onChange={handleSelectAllInGroup}
                              className="w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm font-medium text-gray-800">
                              Select all in {selectedGroup.categoryName}
                            </span>
                          </label>
                          {selectedGroup.products.map((product) => (
                            <label
                              key={product.productId}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={form.productIds.includes(product.productId)}
                                onChange={() =>
                                  handleProductToggle(product.productId)
                                }
                                className="w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
                              />
                              <span className="text-sm text-gray-800">
                                {product.productName || product.name || product.productId}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No products found</p>
                  )}
                  {form.productIds.length === 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      No selection - coupon applies to the whole order
                    </p>
                  )}
                </div>

                {/* Checkboxes */}
                <div className="space-y-3 pt-4 border-t border-slate-200">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="oneTimePerUser"
                      checked={form.oneTimePerUser}
                      onChange={handleChange}
                      className="w-5 h-5 text-orange-600 border-slate-300 rounded focus:ring-orange-500 transition-all"
                    />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      One-time use per customer
                    </span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="perUserPerDay"
                      checked={form.perUserPerDay}
                      onChange={handleChange}
                      className="w-5 h-5 text-orange-600 border-slate-300 rounded focus:ring-orange-500 transition-all"
                    />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      Once per customer per day
                    </span>
                    <span className="text-xs text-gray-500">
                      (e.g. a coupon valid every Monday, usable once each Monday)
                    </span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="visibleToAll"
                      checked={form.visibleToAll}
                      onChange={handleChange}
                      className="w-5 h-5 text-orange-600 border-slate-300 rounded focus:ring-orange-500 transition-all"
                    />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      Visible to all customers
                    </span>
                    <span className="text-xs text-gray-500">
                      (Uncheck to hide - customers can still apply via code)
                    </span>
                  </label>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t border-slate-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setForm(defaultForm);
                    }}
                    className="border-slate-300 text-gray-700 hover:bg-slate-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    {form.id ? 'Update Coupon' : 'Create Coupon'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Search Bar */}
        {!showForm && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
              <Input
                type="text"
                placeholder="Search coupons by code or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-2.5 border-slate-300 focus:ring-orange-500 focus:border-orange-500 font-medium"
              />
            </div>
          </div>
        )}

        {/* Coupons List */}
        <Card className="shadow-lg border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 text-orange-600 animate-spin mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">Loading coupons...</p>
                </div>
              </div>
            ) : filteredCoupons.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Code
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Discount
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Min Order
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Visibility
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Usage
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredCoupons.map((coupon) => (
                      <tr
                        key={coupon.id}
                        className="hover:bg-slate-50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-bold text-gray-900 bg-slate-100 px-3 py-1 rounded">
                              {coupon.code}
                            </code>
                            <button
                              onClick={() => handleCopyCode(coupon.code)}
                              className="text-gray-400 hover:text-orange-600 transition-colors p-1"
                              title="Copy code"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{coupon.name}</p>
                            {coupon.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{coupon.description}</p>
                            )}
                            {coupon.productIds?.length > 0 && (
                              <p className="text-xs text-purple-600 mt-0.5">
                                {coupon.productIds.length} specific product(s)
                              </p>
                            )}
                            {scheduleSummary(coupon) && (
                              <p className="text-xs text-sky-600 mt-0.5">
                                {scheduleSummary(coupon)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-bold text-orange-600">
                            {coupon.couponType === 'PERCENTAGE'
                              ? `${coupon.value}%`
                              : `₹${coupon.value}`}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-700">
                            {coupon.minOrderAmount > 0 ? `₹${coupon.minOrderAmount}` : '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <Badge
                            className={`font-semibold ${
                              coupon.active
                                ? 'bg-green-100 text-green-800 hover:bg-green-100'
                                : 'bg-red-100 text-red-800 hover:bg-red-100'
                            }`}
                          >
                            {coupon.active ? '✓ Active' : '✗ Inactive'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <Badge
                            className={`font-semibold ${
                              coupon.visibleToAll
                                ? 'bg-blue-100 text-blue-800 hover:bg-blue-100'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {coupon.visibleToAll ? '👁 Public' : '🔒 Hidden'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">
                            {coupon.usageCount}
                            {coupon.totalUsageLimit > 0 && ` / ${coupon.totalUsageLimit}`}
                          </p>
                          {coupon.perUserPerDay && (
                            <p className="text-xs text-gray-500">1/day per customer</p>
                          )}
                          {coupon.perUserLimit > 0 && (
                            <p className="text-xs text-gray-500">
                              max {coupon.perUserLimit}/customer
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleToggleVisibility(coupon)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-150"
                              title={coupon.visibleToAll ? 'Hide from list' : 'Show to all'}
                            >
                              {coupon.visibleToAll ? (
                                <Eye className="w-4 h-4" />
                              ) : (
                                <EyeOff className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleEdit(coupon)}
                              className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all duration-150"
                              title="Edit coupon"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(coupon.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-150"
                              title="Delete coupon"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-16 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="p-3 bg-orange-50 rounded-full">
                    <Tag className="w-8 h-8 text-orange-300" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No coupons found</h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Create your first coupon to get started'}
                </p>
                {!searchQuery && vendorId && (
                  <Button
                    onClick={() => openForm()}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Coupon
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="text-center">
              <DialogTitle className="text-lg">Delete Coupon</DialogTitle>
              <DialogDescription className="mt-2">
                Are you sure you want to delete this coupon? This action cannot be undone.
              </DialogDescription>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(deleteConfirmId)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
